/**
 * Eval Scenarios React Query Hooks
 * Data fetching and mutations for evaluation scenarios
 * Matches the original Alpine.js implementation
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { evalScenariosApi } from '@/lib/api/evalScenarios';
import type {
  CreateEvalScenarioRequest,
  UpdateEvalScenarioRequest,
  PromoteChatRequest,
} from '@/types/evalScenarios';

/**
 * Hook to fetch all scenarios with optional filters
 */
export function useEvalScenarios(params?: {
  category?: string;
  source?: string;
  is_active?: boolean;
}) {
  return useQuery({
    queryKey: ['eval-scenarios', params],
    queryFn: () => evalScenariosApi.list(params),
  });
}

/**
 * Hook to fetch a single scenario
 */
export function useEvalScenario(scenarioId: string | null) {
  return useQuery({
    queryKey: ['eval-scenario', scenarioId],
    queryFn: () => evalScenariosApi.get(scenarioId!),
    enabled: !!scenarioId,
  });
}

/**
 * Hook to fetch available categories
 */
export function useEvalCategories() {
  return useQuery({
    queryKey: ['eval-categories'],
    queryFn: () => evalScenariosApi.getCategories(),
  });
}

/**
 * Hook to fetch all unique tags
 */
export function useEvalTags() {
  return useQuery({
    queryKey: ['eval-tags'],
    queryFn: () => evalScenariosApi.getTags(),
  });
}

/**
 * Hook to fetch agent types from API
 */
export function useAgentTypes() {
  return useQuery({
    queryKey: ['eval-agent-types'],
    queryFn: () => evalScenariosApi.getAgentTypes(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to fetch LLM judger models
 */
export function useJudgerModels() {
  return useQuery({
    queryKey: ['eval-judger-models'],
    queryFn: () => evalScenariosApi.getJudgerModels(),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
}

/**
 * Hook to create a new scenario
 */
export function useCreateEvalScenario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateEvalScenarioRequest) => evalScenariosApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eval-scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['eval-categories'] });
    },
  });
}

/**
 * Hook to update a scenario
 */
export function useUpdateEvalScenario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ scenarioId, data }: { scenarioId: string; data: UpdateEvalScenarioRequest }) =>
      evalScenariosApi.update(scenarioId, data),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['eval-scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['eval-scenario', variables.scenarioId] });
    },
  });
}

/**
 * Hook to delete a scenario
 */
export function useDeleteEvalScenario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scenarioId: string) => evalScenariosApi.delete(scenarioId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eval-scenarios'] });
    },
  });
}

/**
 * Hook to duplicate a scenario
 */
export function useDuplicateEvalScenario() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (scenarioId: string) => evalScenariosApi.duplicate(scenarioId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eval-scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['eval-categories'] });
    },
  });
}

/**
 * Hook to promote a chat conversation to a scenario
 */
export function usePromoteChat() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: PromoteChatRequest) => evalScenariosApi.promoteChat(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eval-scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['eval-categories'] });
    },
  });
}

/**
 * Hook to run evaluation for a single scenario
 */
export function useRunEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (scenarioId: string) => {
      console.log('[useRunEvaluation] mutationFn called with scenarioId:', scenarioId);
      try {
        const result = await evalScenariosApi.runSingle(scenarioId);
        console.log('[useRunEvaluation] API call succeeded:', result);
        return result;
      } catch (error) {
        console.error('[useRunEvaluation] API call failed:', error);
        throw error;
      }
    },
    onSuccess: (data) => {
      console.log('[useRunEvaluation] onSuccess:', data);
      queryClient.invalidateQueries({ queryKey: ['eval-scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['eval-runs'] });
    },
    onError: (error) => {
      console.error('[useRunEvaluation] onError:', error);
    },
  });
}

/**
 * Hook to run batch evaluation
 */
export function useRunBatchEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => evalScenariosApi.runBatch(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['eval-scenarios'] });
      queryClient.invalidateQueries({ queryKey: ['eval-runs'] });
    },
  });
}

// ============================================
// Evaluation History Hooks (Phase 2)
// ============================================

/**
 * Hook to fetch historical evaluation results for a scenario
 */
export function useScenarioResults(scenarioId: string | null, limit: number = 20) {
  return useQuery({
    queryKey: ['eval-scenario-results', scenarioId, limit],
    queryFn: () => evalScenariosApi.getScenarioResults(scenarioId!, limit),
    enabled: !!scenarioId,
    staleTime: 30000, // 30 seconds
  });
}

/**
 * Hook to fetch all evaluation runs
 * When autoRefreshOnRunning is true, will auto-refresh every 3s if there are running evals
 */
export function useEvalRuns(limit: number = 20, offset: number = 0, options?: {
  autoRefreshOnRunning?: boolean;
  enabled?: boolean;
}) {
  const { autoRefreshOnRunning = false, enabled = true } = options || {};

  return useQuery({
    queryKey: ['eval-runs', { limit, offset }],
    queryFn: () => evalScenariosApi.listRuns(limit, offset),
    enabled,
    staleTime: 30000, // 30 seconds
    // Conditional refetch: check if data has running evals
    refetchInterval: autoRefreshOnRunning
      ? (query) => {
          const hasRunning = query.state.data?.runs?.some(run => run.status === 'running');
          return hasRunning ? 3000 : false;
        }
      : false,
  });
}

/**
 * Hook to fetch results for a specific run
 */
export function useRunResults(runId: string | null) {
  return useQuery({
    queryKey: ['eval-run-results', runId],
    queryFn: () => evalScenariosApi.getRunResults(runId!),
    enabled: !!runId,
  });
}
